import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice} from '@reduxjs/toolkit'
import * as R from 'ramda'
import type {OmitStrict} from 'type-zoo'
import {recordKeySet} from '@kapitalist/utils/Record'
import * as S from '@kapitalist/utils/Set'
import type {TSkin} from '~/game/skins'
import {skinsHuman} from '~/game/skins'
import type {TGameLobbySize} from '~/pages/GameLobby/types'
import {gameLobbySizeHuman} from '~/pages/GameLobby/types'
import type {AppDispatch} from '~/redux/store'
import {prefersDarkMode} from '~/utils/darkMode'
import type {
  TLocalStorageGetResult,
  TLocalStorageKey,
} from '~/utils/LocalStorageManager'
import {LocalStorageManager} from '~/utils/LocalStorageManager'
import type {TExpectationDescriptor} from '~/utils/runtimeValidation'
import {runtimeTypeValidation} from '~/utils/runtimeValidation'
import type {
  TUserNotificationPerSettingOptions,
  TUserNotificationSettings,
} from '~/utils/UserNotifications'

export type SettingsState = {
  gameLobbySize: TGameLobbySize
  skin: TSkin
  notifications: TUserNotificationSettings
  betaFeatures: TBetaFeatureSettings
  darkMode: boolean
}

export type TBetaFeatureSettings = {
  gameLog: boolean
}

const deserializeGameLobbySize = (
  stored: unknown
): TLocalStorageGetResult<TGameLobbySize> =>
  S.maybeTotalSetHas(stored, recordKeySet(gameLobbySizeHuman))
    ? {result: stored, type: 'success'}
    : {type: 'failure', error: 'not a skin'}

const deserializeSkin = (stored: unknown): TLocalStorageGetResult<TSkin> =>
  S.maybeTotalSetHas(stored, recordKeySet(skinsHuman))
    ? {result: stored, type: 'success'}
    : {type: 'failure', error: 'not a skin'}

const deserializeNotifications = (
  stored: unknown
): TLocalStorageGetResult<Partial<TUserNotificationSettings>> => {
  const perSettingRawExpectation: TExpectationDescriptor<TUserNotificationPerSettingOptions> =
    {
      sound: 'boolean',
      browserNotification: 'boolean',
    }
  const expectations: TExpectationDescriptor<TUserNotificationSettings> = {
    myTurn: [perSettingRawExpectation, 'undefined'],
    someoneElsesTurn: [perSettingRawExpectation, 'undefined'],
    attackFromMe: [perSettingRawExpectation, 'undefined'],
    attackAgainstMe: [perSettingRawExpectation, 'undefined'],
    attackUninvolved: [perSettingRawExpectation, 'undefined'],
    spectatorRequestReceived: [perSettingRawExpectation, 'undefined'],
  }

  if (
    runtimeTypeValidation<Partial<TUserNotificationSettings>>(expectations, stored)
  ) {
    return {result: stored, type: 'success'}
  }

  return R.tap(x => console.log('notification deserialization failed:', x), {
    type: 'failure',
    error: `notifications did not deserialize properly: ${JSON.stringify(stored)}`,
  })
}

const deserializeBetaFeatures = (
  stored: unknown
): TLocalStorageGetResult<Partial<TBetaFeatureSettings>> => {
  const expectations: TExpectationDescriptor<TBetaFeatureSettings> = {
    gameLog: ['boolean', 'undefined'],
  }

  if (runtimeTypeValidation<Partial<TBetaFeatureSettings>>(expectations, stored)) {
    return {result: stored, type: 'success'}
  }

  return R.tap(
    x => console.log('beta feature settings deserialization failed:', x),
    {
      type: 'failure',
      error: `beta feature settings did not deserialize properly: ${JSON.stringify(
        stored
      )}`,
    }
  )
}

// `darkMode` is excluded because its default is the OS preference, which needs a DOM and so
// can't be read at module load. `readPersistedSettings` defaults it via `prefersDarkMode()`.
const defaultSettings: OmitStrict<SettingsState, 'darkMode'> = {
  gameLobbySize: 'small',
  skin: 'bigTech',
  notifications: {
    myTurn: {sound: true, browserNotification: true},
    someoneElsesTurn: {sound: true, browserNotification: false},

    attackFromMe: {sound: true, browserNotification: false},
    attackAgainstMe: {sound: true, browserNotification: true},
    attackUninvolved: {sound: true, browserNotification: false},

    spectatorRequestReceived: {sound: true, browserNotification: false},
  },
  betaFeatures: {
    gameLog: false,
  },
}

/**
 * Called lazily rather than at module load, so importing this reducer doesn't require a browser
 * environment just to read `localStorage`.
 */
const readPersistedSettings = (): SettingsState => {
  const gameLobbySizeResult = LocalStorageManager.mapSuccess(
    LocalStorageManager.get('settings:gameLobbySize'),
    deserializeGameLobbySize
  )

  const skinResult = LocalStorageManager.mapSuccess(
    LocalStorageManager.get('settings:skin'),
    deserializeSkin
  )

  const notificationsResult = LocalStorageManager.mapSuccess(
    LocalStorageManager.getObject('settings:notifications'),
    deserializeNotifications
  )

  const betaFeaturesResult = LocalStorageManager.mapSuccess(
    LocalStorageManager.getObject('settings:betaFeatures'),
    deserializeBetaFeatures
  )

  return {
    gameLobbySize: LocalStorageManager.successOr(
      gameLobbySizeResult,
      defaultSettings.gameLobbySize
    ),
    skin: LocalStorageManager.successOr(skinResult, defaultSettings.skin),
    notifications: {
      ...defaultSettings.notifications,
      ...LocalStorageManager.successOr(
        notificationsResult,
        defaultSettings.notifications
      ),
    },
    betaFeatures: {
      ...defaultSettings.betaFeatures,
      ...LocalStorageManager.successOr(
        betaFeaturesResult,
        defaultSettings.betaFeatures
      ),
    },
    // falls back to the OS preference, same as `~/index.tsx`'s pre-render paint
    darkMode: LocalStorageManager.successOr(
      LocalStorageManager.getObject('settings:darkMode'),
      prefersDarkMode()
    ),
  }
}

// `createSlice` calls this when the store is created, not at import, which is what keeps the
// `localStorage` reads off the import path. Note the resets (`resetSettings`, `_ROOT_LOGOUT`)
// therefore re-read `localStorage`, so settings changed mid-session survive a logout.
const initialState = readPersistedSettings

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setGameLobbySize(state: SettingsState, action: PayloadAction<TGameLobbySize>) {
      const gameLobbySize = action.payload
      LocalStorageManager.set('settings:gameLobbySize', gameLobbySize)
      return {...state, gameLobbySize}
    },
    setSkin(state: SettingsState, action: PayloadAction<TSkin>) {
      const skin = action.payload
      LocalStorageManager.set('settings:skin', skin)
      return {...state, skin}
    },
    setNotifications(
      state: SettingsState,
      action: PayloadAction<TUserNotificationSettings>
    ) {
      const notifications = action.payload
      LocalStorageManager.setObject('settings:notifications', notifications)
      return {...state, notifications}
    },
    setBetaFeatures(
      state: SettingsState,
      action: PayloadAction<TBetaFeatureSettings>
    ) {
      const betaFeatures = action.payload
      LocalStorageManager.setObject('settings:betaFeatures', betaFeatures)
      return {...state, betaFeatures}
    },
    setDarkMode(state: SettingsState, action: PayloadAction<boolean>) {
      const darkMode = action.payload
      LocalStorageManager.setObject('settings:darkMode', darkMode)
      // The DOM write lives in `~/index.tsx`'s store subscription: reducers stay pure, and a
      // write here would miss `_ROOT_LOGOUT`, which recomputes `darkMode` without going through
      // this reducer.
      return {...state, darkMode}
    },
    resetSettings() {
      return readPersistedSettings()
    },
  },
})

const tryJSONParse = (v: any): unknown => {
  try {
    return JSON.parse(v)
  } catch {
    return v
  }
}

/**
 * Syncs settings across tabs: another tab writing to `localStorage` fires a `storage` event here,
 * which we mirror into this tab's redux store. Call once at app startup (see `~/index.tsx`).
 *
 * @returns a teardown that removes the listener (useful for tests; prod doesn't need it).
 */
export const initSettingsSync = (dispatch: AppDispatch): (() => void) => {
  const onStorage = (event: StorageEvent) => {
    const skinKey: TLocalStorageKey = 'settings:skin'
    const gameLobbySizeKey: TLocalStorageKey = 'settings:gameLobbySize'
    const notificationsKey: TLocalStorageKey = 'settings:notifications'
    const betaFeaturesKey: TLocalStorageKey = 'settings:betaFeatures'

    if (event.key === gameLobbySizeKey) {
      LocalStorageManager.whenSuccessful(
        deserializeGameLobbySize(event.newValue),
        size => dispatch(settingsSlice.actions.setGameLobbySize(size))
      )
    } else if (event.key === skinKey) {
      LocalStorageManager.whenSuccessful(deserializeSkin(event.newValue), skin =>
        dispatch(settingsSlice.actions.setSkin(skin))
      )
    } else if (event.key === notificationsKey) {
      LocalStorageManager.whenSuccessful(
        deserializeNotifications(tryJSONParse(event.newValue)),
        notifs =>
          dispatch(
            settingsSlice.actions.setNotifications({
              ...defaultSettings.notifications,
              ...notifs,
            })
          )
      )
    } else if (event.key === betaFeaturesKey) {
      LocalStorageManager.whenSuccessful(
        deserializeBetaFeatures(tryJSONParse(event.newValue)),
        notifs =>
          dispatch(
            settingsSlice.actions.setBetaFeatures({
              ...defaultSettings.betaFeatures,
              ...notifs,
            })
          )
      )
    }
  }

  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}

const {
  setGameLobbySize,
  setSkin,
  setNotifications,
  setBetaFeatures,
  setDarkMode,
  resetSettings,
} = settingsSlice.actions
export {
  setGameLobbySize,
  setSkin,
  setNotifications,
  setBetaFeatures,
  setDarkMode,
  resetSettings,
}
export default settingsSlice.reducer
